import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Clock, User } from "lucide-react";

import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/Shared/EmptyState";
import { Dater } from "@/components/Shared/Dater";
import { IRouterOrder, IRouterOrderTimelineEvent } from "@/interfaces/router";
import { useGetRouterOrderTimelineQuery } from "@/api/institutional/orders_api";
import useErrorListener from "@/hooks/useErrorListener";

const TIMELINE_EVENT_DESCRIPTIONS: Record<string, string> = {
  ORDER_CREATED: "The order was created and the quote was accepted.",
  STATUS_CHANGED: "The status of the order was updated.",
  INVOICE_UPLOADED: "An invoice was uploaded for this order.",
  INVOICE_ACCEPTED_CHANGED: "The invoice review status was updated.",
  BENEFICIARY_CHANGED: "The beneficiary of the order was changed.",
  CLIENT_EXCHANGE_RATE_MODIFIED:
    "The exchange rate offered to the client was modified.",
  INTERMEDIATE_TX_HASH_SAVED:
    "An intermediate transaction hash was recorded for this order.",
  QUOTE_ROLLED_OVER: "The quote was rolled over.",
  LOSS_RECORDED: "A loss was recorded for this order.",
  DESTINATION_TRANSFER_ID_RECORDED: "The destination transfer ID was recorded.",
  FUNDS_RETURNED: "Funds were returned to the sender.",
  TX_RETRY: "The transaction was retried.",
};

const getEventDescription = (item: IRouterOrderTimelineEvent, t: TFunction) => {
  if (item.event === "STATUS_CHANGED" && item.status_from && item.status_to) {
    return t("Status changed from {{from}} to {{to}}", {
      from: t(item.status_from),
      to: t(item.status_to),
    });
  }
  const key = TIMELINE_EVENT_DESCRIPTIONS[item.event];
  return key ? t(key) : null;
};

export const OrderTimelineModal = ({
  data: order,
}: {
  data?: IRouterOrder;
}) => {
  const { t } = useTranslation();

  const {
    data: timeline = [],
    isLoading,
    error,
  } = useGetRouterOrderTimelineQuery(
    { order_id: order?.id ?? "" },
    {
      skip: !order?.id,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );

  useErrorListener({ error });

  return (
    <SheetContent
      side="right"
      className="w-full sm:max-w-md flex flex-col gap-0 p-0"
    >
      <SheetHeader>
        <SheetTitle>{t("Order Timeline")}</SheetTitle>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            {t("Order ID")}
          </span>
          <span className="text-xs text-muted-foreground font-mono truncate">
            {order?.id}
          </span>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="space-y-4 py-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : timeline.length === 0 ? (
          <EmptyState
            icon={Clock}
            title={t("No timeline events")}
            description={t("There are no events recorded for this order yet.")}
          />
        ) : (
          <ol className="relative border-l border-border/60 ml-4 space-y-6 py-2">
            {timeline.map((item, index) => {
              const description = getEventDescription(item, t);
              return (
                <li key={index} className="ml-6">
                  <span className="absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 ring-4 ring-background">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  </span>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        {t(item.event)}
                      </p>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        <Dater>{item.time_event}</Dater>
                      </span>
                    </div>
                    {description && (
                      <p className="text-sm text-muted-foreground">
                        {description}
                      </p>
                    )}
                    {item.actor && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span>{item.actor}</span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </SheetContent>
  );
};

export default OrderTimelineModal;
